import { toast } from '@bozic/ui/toast';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getType, type ActionType } from 'typesafe-actions';

import { getAuthState, getEmail } from '@/shared/authentication/slice/selectors';
import { AuthState } from '@/shared/authentication/utils/constants';
import { isAuthorized } from '@/shared/authorization/isAuthorized';
import { Logger } from '@/shared/logger';

import { getErrorMessage } from '@/utils/invariant';

import SortDirection from '../../../enums/SortDirection';
import insuranceAPI from '../../../services/insuranceService';
import sortObjectByKey from '../../../utils/sortObjectByKey';
import { getAdminPageAgency } from '../../adminPage/selectors';
import { loadersActions } from '../../loaders/actions';
import { adminActions } from '../actions';
import { getOperations } from '../selectors';
import { agencyOperationsParams } from '../types';

import { addAllOperations, newOperation, removeOperation, updateOperation } from './slice';

import type { Agency } from '../../../types/Agency';
import type { OperationDto } from '../../../types/dto/OperationDto';
import type { Operation } from '../../../types/Operation/Operation';
import type { AxiosResponse } from 'axios';

export function* fetchOperations(
  action: ActionType<typeof adminActions.operations.getAgencyOperations>
) {
  try {
    yield put(loadersActions.adminOperations.activate());
    yield put(loadersActions.operationList.activate());
    let { userName } = action.payload;
    if (!userName) userName = yield select(getEmail);
    let { userRole } = action.payload;
    if (!userRole) userRole = yield select(getAuthState);
    let operations: AxiosResponse<OperationDto[]> | undefined;
    if (userName && userRole) {
      if (isAuthorized(userRole, [AuthState.producer, AuthState.stakeholder])) {
        operations = yield call(
          insuranceAPI.getProducerOperations,
          userName,
          action.payload.bindingRequirements
        );
      } else if (isAuthorized(userRole, [AuthState.globalAdministrator])) {
        operations = yield call(
          insuranceAPI.getAgencyOperations,
          action.payload.agencyName,
          action.payload.bindingRequirements
        );
      } else if (
        isAuthorized(userRole, [AuthState.insuranceAgent, AuthState.insuranceAgentAdministrator])
      ) {
        operations = yield call(
          insuranceAPI.getAgentOperations,
          userName,
          action.payload.bindingRequirements
        );
      }
      if (operations) {
        yield put(addAllOperations(operations.data));
      }
    }
    yield put(loadersActions.adminOperations.disable());
    yield put(loadersActions.operationList.disable());
  } catch (error: unknown) {
    yield put(loadersActions.adminOperations.disable());
    yield put(loadersActions.operationList.disable());
    toast.error(error instanceof Error ? error.message : 'Error fetching operations', {
      position: 'top-right'
    });
  }
}

function* createAgencyOperation(action: ActionType<typeof adminActions.operations.addOperation>) {
  try {
    yield put(loadersActions.spinner.activate());
    const operationName: AxiosResponse<{ operationName: string }> = yield call(
      insuranceAPI.createAgencyOperation,
      action.meta.agencyName,
      action.payload
    );

    yield put(newOperation({ ...action.payload, name: operationName.data.operationName }));
    yield put(loadersActions.spinner.disable());
    action.meta.cb();
    yield put(
      adminActions.operations.sort({ sort: action.meta.sort, direction: action.meta.direction })
    );
    toast.success('Operation created');
  } catch (error: unknown) {
    yield action.meta.cb();
    yield put(loadersActions.spinner.disable());
    toast.error(error instanceof Error ? error.message : 'Error creating operation', {
      position: 'top-right'
    });
  }
}

function* editAgencyOperation(action: ActionType<typeof adminActions.operations.editOperation>) {
  try {
    const agency: Agency | null = yield select(getAdminPageAgency);
    const operationName: AxiosResponse<{ operationName: string }> = yield call(
      insuranceAPI.editAgencyOperation,
      agency?.name ?? '',
      action.meta.name,
      { ...action.payload, primaryContact: action.meta.primaryContact }
    );

    yield put(
      updateOperation({
        operation: { ...action.payload, name: operationName.data.operationName },
        name: action.meta.name
      })
    );
    yield toast.success('Successfully edited operation.');
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Error editing operation');
    toast.error(message);
    Logger.trace(message, { level: 'Error', sentryLogging: true, extra: { error } });
  } finally {
    action.meta.cb();
  }
}

function* deleteAgencyOperation(
  action: ActionType<typeof adminActions.operations.deleteOperation>
) {
  try {
    yield put(loadersActions.spinner.activate());

    const operations: Operation[] = yield select(getOperations);
    const selectedOperation = operations.find(
      ({ name }: { name: string }) => name === action.payload
    );
    const OperationFullName = selectedOperation?.fullName || action.payload;

    const agency: Agency | null = yield select(getAdminPageAgency);
    yield call(insuranceAPI.deleteAgencyOperation, agency?.name ?? '', action.payload);
    yield put(removeOperation(action.payload));
    yield put(loadersActions.spinner.disable());
    action.meta();
    toast.success(`Successfully deleted operation ${OperationFullName}`);
  } catch (error) {
    action.meta();
    yield put(loadersActions.spinner.disable());
    console.log(error);
  }
}

export function* sortOperations({
  payload: { sort, direction }
}: ActionType<typeof adminActions.operations.sort>) {
  try {
    const sortKey = sort || 'fullName';
    const sortDirection = direction || SortDirection.ASCENDING;

    const operations: Operation[] = yield select(getOperations);

    operations.sort(sortObjectByKey(sortKey as keyof Operation, sortDirection));

    yield put(addAllOperations([...operations]));
  } catch (error) {
    console.log(error);
  }
}

function* editOperationInsuranceProgram(
  action: ActionType<typeof adminActions.operations.editOperationInsuranceProgram>
) {
  try {
    const operations: Operation[] = yield select(getOperations);
    const agency: Agency | null = yield select(getAdminPageAgency);
    const operation = operations.find(o => o.fullName === action.payload.fullName);
    const operationName: AxiosResponse<{ operationName: string }> = yield call(
      insuranceAPI.editAgencyOperation,
      agency?.name ?? '',
      action.meta.name,
      action.payload
    );
    action.meta.cb();

    yield put(
      updateOperation({
        operation: { ...action.payload, name: operationName.data.operationName },
        name: action.meta.name
      })
    );
    if (
      operation &&
      operation.insurancePrograms.includes('LGM') !==
        action.payload.insurancePrograms.includes('LGM')
    ) {
      yield toast.success(
        `Livestock Gross Margin ${
          action.payload.insurancePrograms.includes('LGM') ? 'enabled' : 'disabled'
        } for ${operation.fullName}.`
      );
    }
    if (
      operation &&
      operation.insurancePrograms.includes('DRP') !==
        action.payload.insurancePrograms.includes('DRP')
    ) {
      yield toast.success(
        `Dairy Revenue Protection ${
          action.payload.insurancePrograms.includes('DRP') ? 'enabled' : 'disabled'
        } for ${operation.fullName}.`
      );
    }
    if (
      operation &&
      operation.insurancePrograms.includes('LRP') !==
        action.payload.insurancePrograms.includes('LRP')
    ) {
      yield toast.success(
        `Livestock Risk Protection ${
          action.payload.insurancePrograms.includes('LRP') ? 'enabled' : 'disabled'
        } for ${operation.fullName}.`
      );
    }
  } catch (error: unknown) {
    toast.error(error instanceof Error ? error.message : 'Error editing operation', {
      position: 'top-right'
    });
  }
}

function* editAllOperations(action: ActionType<typeof adminActions.operations.editAllOperations>) {
  const agency: Agency | null = yield select(getAdminPageAgency);
  const agencyName: string = agency?.name ?? '';
  try {
    yield all(
      action.payload.map(operation =>
        call(insuranceAPI.editAgencyOperation, agencyName, operation.name, operation)
      )
    );
    yield put(adminActions.operations.getAgencyOperations(agencyOperationsParams(agencyName)));

    action.meta();
    toast.success('Operations Edited');
  } catch (e) {
    console.error(e);
  }
}

export function* operationsSagaWatcher() {
  yield all([
    takeLatest(getType(adminActions.operations.getAgencyOperations), fetchOperations),
    takeLatest(getType(adminActions.operations.addOperation), createAgencyOperation),
    takeLatest(getType(adminActions.operations.editOperation), editAgencyOperation),
    takeLatest(
      getType(adminActions.operations.editOperationInsuranceProgram),
      editOperationInsuranceProgram
    ),
    takeLatest(getType(adminActions.operations.deleteOperation), deleteAgencyOperation),
    takeLatest(getType(adminActions.operations.sort), sortOperations),
    takeLatest(getType(adminActions.operations.editAllOperations), editAllOperations)
  ]);
}
